import type { XRPC } from '@atcute/client';
import type { FeedPost } from '../domain/types';
import { createRecord, deleteRecord } from './authenticated';

interface ProfileCommentsResponse {
  comments?: FeedPost[];
  comment?: FeedPost;
  error?: string;
}

export function profileSubjectUri(ownerDid: string): string {
  return `at://${ownerDid}/app.bsky.actor.profile/self`;
}

async function profileCommentRequest(
  body: Record<string, unknown>,
): Promise<ProfileCommentsResponse> {
  const response = await fetch('../profile-comments.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json() as ProfileCommentsResponse;
  if (!response.ok) {
    throw new Error(data.error || 'The SkySpace comment could not be published.');
  }
  return data;
}

export async function getProfileComments(
  ownerDid: string,
  signal?: AbortSignal,
): Promise<FeedPost[]> {
  const params = new URLSearchParams({ owner: ownerDid });
  const response = await fetch(`../profile-comments.php?${params}`, {
    signal,
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const data = await response.json() as ProfileCommentsResponse;
  if (!response.ok) {
    throw new Error(data.error || 'The SkySpace comments could not be loaded.');
  }
  return Array.isArray(data.comments) ? data.comments : [];
}

export async function publishProfileComment(
  rpc: XRPC,
  authorDid: string,
  ownerDid: string,
  text: string,
): Promise<FeedPost | undefined> {
  const value = text.trim().slice(0, 240);
  if (!value) throw new Error('Write a comment first.');
  const created = await createRecord(rpc, authorDid, 'me.skyspace.profile.comment', {
    $type: 'me.skyspace.profile.comment',
    subject: profileSubjectUri(ownerDid),
    text: value,
    createdAt: new Date().toISOString(),
  }, false);
  try {
    const response = await profileCommentRequest({
      action: 'publish',
      owner: ownerDid,
      recordUri: created.uri,
    });
    return response.comment;
  } catch (reason) {
    await deleteRecord(rpc, authorDid, created.uri).catch(() => undefined);
    throw reason;
  }
}
